import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { apiClient } from "../../api/client";
import { url } from "../../api";
import { useAppSelector } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { getAuthToken } from "../../utils/authTokens";

// types
interface EligibleAOTD {
    _id: string;
    title: string;
    date: string;
    emojis: string[];
    authorQuote: string;
    filename: string;
}

interface SelectionData {
    aotdId: string;
    selectedAt: string;
}

interface EligibleResponse {
    success: boolean;
    data: {
        eligible: EligibleAOTD[];
        selection: SelectionData | null;
        deadline: string;
        deadlinePassed: boolean;
    };
    error?: string;
}

// animations (matching gallery)
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(102, 51, 153, 0.7); }
  50% { box-shadow: 0 0 0 8px rgba(102, 51, 153, 0); }
`;

// styled components (matching gallery styles)
const PageContainer = styled.div`
  min-height: 100vh;
  height: auto;
`;

const HeaderSection = styled.div`
  padding: 20px;
  text-align: center;
`;

const Title = styled.div`
  font-size: 2rem;
  color: #222;
  text-align: center;
  font-weight: 600;
  padding: 10px;
`;

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: #666;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 1rem;
  line-height: 1.5;
`;

const DeadlineNotice = styled.div<{ isPassed: boolean }>`
  color: ${({ isPassed }) => (isPassed ? "#c62828" : "#666")};
  padding: 8px 20px;
  margin: 0 auto 1rem;
  text-align: center;
  max-width: 400px;
  font-size: 0.85rem;
`;

const CurrentSelectionBanner = styled.div`
  color: #333;
  padding: 12px 20px;
  margin: 0 auto 1rem;
  text-align: center;
  max-width: 450px;
  
  h3 {
    margin: 0 0 4px 0;
    font-size: 1rem;
    color: #663399;
  }
  
  p {
    margin: 0;
    font-size: 0.85rem;
  }

  span {
    font-size: 0.8rem;
    color: #666;
  }
`;

const ErrorMessage = styled.div`
  color: #c62828;
  padding: 8px 20px;
  margin: 0 auto 0.5rem;
  text-align: center;
  max-width: 400px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #2e7d32;
  padding: 8px 20px;
  margin: 0 auto 0.5rem;
  text-align: center;
  max-width: 400px;
  font-size: 0.9rem;
`;

// grid matching gallery
const ArtGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  padding: 10px;
  align-items: center;
  gap: 5px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    width: 95%;
    margin: 0 auto;
  }
`;

// spinner matching gallery
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto;
  height: auto;
  min-height: 350px;
`;

const Spinner = styled.div`
  border: 16px solid #f3f3f3;
  border-radius: 5%;
  border-top: 16px solid #2e2e2e;
  width: 30px;
  height: 30px;
  animation: ${spin} 2s linear infinite;
`;

const LoadingText = styled.p`
  margin-top: 15px;
  color: #666;
  text-align: center;
`;

// art container matching gallery
const ArtContainer = styled.div<{ isSelected: boolean }>`
  width: auto;
  height: auto;
  position: relative;
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  border: ${({ isSelected }) => (isSelected ? "4px solid #663399" : "4px solid transparent")};
  animation: ${({ isSelected }) => (isSelected ? pulse : "none")} 2s infinite;

  &:hover {
    /* transform: scale(1.02); */
  }
`;

const ArtImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
`;

const ArtTitle = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 10px;
  left: 10px;
  color: #fff;
  padding: 10px;
  font-weight: bold;
  font-size: 1.5rem;
  text-shadow: -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black;
  display: ${({ isVisible }) => (isVisible ? "none" : "block")};
`;

const ArtDate = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 10px;
  left: 10px;
  color: #fff;
  padding: 10px;
  font-weight: bold;
  text-shadow: -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black;
  display: ${({ isVisible }) => (isVisible ? "none" : "flex")};
  align-items: center;
  gap: 8px;
`;

const ArtQuote = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  padding: 10px;
  text-align: center;
  font-weight: bold;
  text-shadow: -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black;
  display: ${({ isVisible }) => (isVisible ? "none" : "block")};
  max-width: 80%;
`;

const ArtEmojis = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 35px;
  left: 10px;
  color: #fff;
  padding: 10px;
  font-size: 1.2rem;
  font-weight: bold;
  text-shadow: 
    -1px 0 black, 0 1px black, 1px 0 black, 0 -1px black,
    2px 2px 2px white, -2px -2px 2px white, 2px -2px 2px white, -2px 2px 2px white;
  z-index: 2;
  display: ${({ isVisible }) => (isVisible ? "none" : "block")};
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #663399;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 3;
`;

const SelectButton = styled.button<{ isSelected: boolean }>`
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ isSelected }) => (isSelected ? "#4caf50" : "#663399")};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  z-index: 3;

  ${ArtContainer}:hover & {
    opacity: 1;
  }

  &:hover {
    transform: translateX(-50%) scale(1.05);
  }

  &:disabled {
    background: #999;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;

  h2 {
    color: #333;
    margin-bottom: 10px;
  }

  p {
    max-width: 400px;
    line-height: 1.6;
    margin: 0 auto;
  }
`;

const BackButton = styled.button`
  background: #f0f0f0;
  color: #333;
  border: 1px solid #ccc;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 10px;

  &:hover {
    background: #e0e0e0;
  }
`;

// lazy art component with intersection observer
const LazyArt: React.FC<{
    aotd: EligibleAOTD;
    isSelected: boolean;
    onSelect: (id: string) => void;
    disabled: boolean;
}> = ({ aotd, isSelected, onSelect, disabled }) => {
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
        rootMargin: "300px 0px",
    });

    return (
        <div ref={ref} style={{ minHeight: "350px" }}>
            {inView ? (
                <ArtCardComponent
                    aotd={aotd}
                    isSelected={isSelected}
                    onSelect={onSelect}
                    disabled={disabled}
                />
            ) : null}
        </div>
    );
};

// individual art card (matching gallery Art component)
const ArtCardComponent: React.FC<{
    aotd: EligibleAOTD;
    isSelected: boolean;
    onSelect: (id: string) => void;
    disabled: boolean;
}> = ({ aotd, isSelected, onSelect, disabled }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [fetched, setFetched] = useState(false);
    const [touchCount, setTouchCount] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    // mobile detection
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.matchMedia("(max-width: 768px)").matches);
        };
        window.addEventListener("resize", checkIfMobile);
        checkIfMobile();
        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);

    // fetch image
    useEffect(() => {
        const fetchImage = async () => {
            try {
                const response = await apiClient.get(
                    `${url}/api/art-of-the-day/image-small/${aotd.filename}`,
                    {
                        responseType: "blob",
                        headers: {
                            Authorization: `Bearer ${getAuthToken()}`,
                        },
                    }
                );
                if (response.status === 200) {
                    setImageUrl(URL.createObjectURL(response.data));
                    setFetched(true);
                }
            } catch (error) {
                console.error("Error loading image:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchImage();

        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [aotd.filename]);

    const handleClick = () => {
        if (isMobile) {
            setTouchCount((prev) => prev + 1);
        }
    };

    const handleSelect = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!disabled) {
            onSelect(aotd._id);
        }
    };

    if (!fetched && !loading) return null;
    if (loading) {
        return (
            <LoadingContainer>
                <Spinner />
            </LoadingContainer>
        );
    }

    const dateObj = new Date(aotd.date);
    const isInfoHidden = (!isMobile && isHovered) || (isMobile && touchCount % 2 !== 0);

    return (
        <ArtContainer
            isSelected={isSelected}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick}
        >
            {isSelected && <SelectedBadge>✓ Your Pick</SelectedBadge>}
            {imageUrl && <ArtImage src={imageUrl} alt={aotd.title} />}
            <ArtTitle isVisible={isInfoHidden}>{aotd.title}</ArtTitle>
            <ArtDate isVisible={isInfoHidden}>
                <span>{dateObj.toLocaleDateString()}</span>
            </ArtDate>
            {aotd.emojis && aotd.emojis.length > 0 && (
                <ArtEmojis isVisible={isInfoHidden}>{aotd.emojis.join(" ")}</ArtEmojis>
            )}
            {aotd.authorQuote && (
                <ArtQuote isVisible={isInfoHidden}>{aotd.authorQuote}</ArtQuote>
            )}
            <SelectButton
                isSelected={isSelected}
                disabled={disabled}
                onClick={handleSelect}
                style={{ opacity: isMobile ? 1 : undefined }}
            >
                {isSelected ? "Selected ✓" : "Select"}
            </SelectButton>
        </ArtContainer>
    );
};

// main component
const AOTD2025Favorite: React.FC = () => {
    const navigate = useNavigate();
    const auth = useAppSelector((state) => state.auth);

    const [loading, setLoading] = useState(true);
    const [eligible, setEligible] = useState<EligibleAOTD[]>([]);
    const [selection, setSelection] = useState<string | null>(null);
    const [selectionDate, setSelectionDate] = useState<Date | null>(null);
    const [deadline, setDeadline] = useState<Date | null>(null);
    const [deadlinePassed, setDeadlinePassed] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // fetch eligible AOTDs
    const fetchEligible = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get<EligibleResponse>(
                `${url}/api/art-of-the-day/2025-favorites/eligible`,
                {
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`,
                    },
                }
            );

            if (response.data.success) {
                setEligible(response.data.data.eligible);
                setSelection(response.data.data.selection?.aotdId || null);
                setSelectionDate(response.data.data.selection?.selectedAt ? new Date(response.data.data.selection.selectedAt) : null);
                setDeadline(new Date(response.data.data.deadline));
                setDeadlinePassed(response.data.data.deadlinePassed);
            } else {
                setError(response.data.error || "Failed to load eligible AOTDs");
            }
        } catch (err: any) {
            console.error("Error fetching eligible AOTDs:", err);
            setError(err.response?.data?.error || "Failed to load eligible AOTDs");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = "Pick Your Favorite 2025 AOTD";
        if (auth.bootstrapStatus === "ready" && auth.token) {
            fetchEligible();
        }
    }, [auth.bootstrapStatus, auth.token, fetchEligible]);

    // handle selection
    const handleSelect = async (aotdId: string) => {
        if (deadlinePassed || submitting) return;

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);

            const response = await apiClient.post(
                `${url}/api/art-of-the-day/2025-favorites/select`,
                { aotdId },
                {
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.success) {
                setSelection(aotdId);
                setSelectionDate(new Date());
                setSuccess("Your favorite has been saved!");
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.data.error || "Failed to save selection");
            }
        } catch (err: any) {
            console.error("Error saving selection:", err);
            setError(err.response?.data?.error || "Failed to save selection");
        } finally {
            setSubmitting(false);
        }
    };

    // format deadline
    const deadlineStr = deadline
        ? deadline.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })
        : "February 1, 2026";

    // find selected AOTD
    const selectedAOTD = eligible.find((a) => a._id === selection);

    // auth bootstrap in progress
    if (auth.bootstrapStatus === "loading") {
        return (
            <PageContainer>
                <HeaderSection>
                    <BackButton onClick={() => navigate("/art-of-the-day")}>
                        ← Back to AOTD
                    </BackButton>
                    <LoadingContainer>
                        <div style={{ textAlign: "center" }}>
                            <Spinner />
                            <LoadingText>Restoring your session...</LoadingText>
                        </div>
                    </LoadingContainer>
                </HeaderSection>
            </PageContainer>
        );
    }

    // not authenticated
    if (auth.bootstrapStatus === "ready" && !auth.token) {
        return (
            <PageContainer>
                <HeaderSection>
                    <BackButton onClick={() => navigate("/art-of-the-day")}>
                        ← Back to AOTD
                    </BackButton>
                    <EmptyState>
                        <h2>Sign In Required</h2>
                        <p>Please sign in to pick your favorite 2025 AOTD.</p>
                    </EmptyState>
                </HeaderSection>
            </PageContainer>
        );
    }

    // loading state
    if (loading) {
        return (
            <PageContainer>
                <HeaderSection>
                    <BackButton onClick={() => navigate("/art-of-the-day")}>
                        ← Back to AOTD
                    </BackButton>
                    <Title>Pick Your Favorite 2025 AOTD</Title>
                </HeaderSection>
                <LoadingContainer>
                    <div style={{ textAlign: "center" }}>
                        <Spinner />
                        <LoadingText>Loading your 2025 AOTDs...</LoadingText>
                    </div>
                </LoadingContainer>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <HeaderSection>
                <BackButton onClick={() => navigate("/art-of-the-day")}>
                    ← Back to AOTD
                </BackButton>
                <Title>Your Favorite 2025 AOTD</Title>
                <Subtitle>
                    choose your favorite AOTD from 2025 that you <strong>created with emojis</strong>.
                    your selection will be featured in the <strong>2025 AOTD poster</strong>.
                </Subtitle>

                <DeadlineNotice isPassed={deadlinePassed}>
                    {deadlinePassed
                        ? `selection period has ended (${deadlineStr})`
                        : `you can change your selection until ${deadlineStr}. if you don't select one until then, your favorite will be chosen randomly.`}
                </DeadlineNotice>

                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}

                {selection && selectedAOTD && (
                    <CurrentSelectionBanner>
                        <h3>Your Current Selection</h3>
                        <p>
                            {selectedAOTD.title} ({selectedAOTD.emojis.join(" ")})
                        </p>
                        <span>
                            created on {new Date(selectedAOTD.date).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                            {/* {selectionDate && (
                                <> · selected on {selectionDate.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}</>
                            )} */}
                        </span>
                    </CurrentSelectionBanner>
                )}
            </HeaderSection>

            {eligible.length === 0 ? (
                <EmptyState>
                    <h2>No Eligible AOTDs</h2>
                    <p>
                        You don't have any AOTDs from 2025 that were created with emojis.
                        Only AOTDs where you used at least one emoji are eligible for selection.
                    </p>
                </EmptyState>
            ) : (
                <ArtGridContainer>
                    {eligible.map((aotd) => (
                        <LazyArt
                            key={aotd._id}
                            aotd={aotd}
                            isSelected={selection === aotd._id}
                            onSelect={handleSelect}
                            disabled={deadlinePassed || submitting}
                        />
                    ))}
                </ArtGridContainer>
            )}
        </PageContainer>
    );
};

export default AOTD2025Favorite;
